import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

// classes
import FlightPlan from "classes/flightPlan";
import Aircraft from "classes/aircraft";
import { Staff } from "classes/Staff";
import Flight from "classes/flight";
import Slot from "classes/slot";

//api
import api from "api/api";

const useLoadGameData = options => {
	const [dataLoaded, setDataLoaded] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [i, setI] = useState(0);

	const mapInfo = useSelector(state => state.map);
	const dispatch = useDispatch();

	const retry = () => {
		setI(i => i + 1);
		setIsLoading(true);
		setError(null);

		if (typeof options.onRetry === "function") options.onRetry();
	};

	useEffect(() => {
		if (dataLoaded) return;
		load();

		async function load() {
			try {
				const [flightPlans] = await Promise.all([
					loadFlightPlans(),
					fetchGroup(),
					fetchAirline(),
					fetchAndLoadFleet(),
					fetchAndLoadCateringData(),
					loadSlots(),
					loadStaff(),
					loadConfigurations(),
					fetchStaffRecruitments(),
					fetchDemand()
				]);

				handleFlightPlans(...flightPlans);

				setIsLoading(false);
				setDataLoaded(true);
			} catch (error) {
				console.error(error);
				setError(error);
				setIsLoading(false);
				setDataLoaded(false);
			}
		}

		async function fetchAndLoadFleet() {
			try {
				const { data } = await axios.get(`${api}/aircraft`);
				dispatch({ type: "SET_FLEET", payload: data.map(a => new Aircraft(a)) });
			} catch (error) {
				throw error;
			}
		}

		async function fetchAirline() {
			try {
				const { data } = await axios.get(`${api}/airline`);
				const airlineData = data.airlineInfo;
				if (!mapInfo.type) dispatch({ type: "MAP_TYPE_SET", state: +airlineData.info.map || 1 });

				dispatch({ type: "AIRLINE_INFO_SET", state: airlineData });
			} catch (error) {
				throw error;
			}
		}

		async function fetchGroup() {
			try {
				const { data } = await axios.get(`${api}/group`);
				const groupData = data[0];

				groupData.fuelSupplier = groupData.fuel_supplier_ID - 1; // The DB is 1-based, but the JS is 0 based. 0 from db means null.
				groupData.fuelSupplier === -1 && (groupData.fuelSupplier = null);

				delete groupData.fuel_supplier_ID;

				dispatch({ type: "GROUP_INFO_SET", state: groupData });
				dispatch({ type: "SET_MONEY", state: +groupData.money });
				dispatch({ type: "LANGUAGE_SET", state: groupData.language.toUpperCase() });
			} catch (error) {
				throw error;
			}
		}

		async function fetchDemand() {
			try {
				const { data } = await axios.get(`${api}/group/demand`);
				dispatch({ type: "SET_DEMAND", payload: data });
			} catch (error) {
				throw error;
			}
		}

		async function fetchStaffRecruitments() {
			try {
				const { data } = await axios.get(`${api}/staff/recruitment`);
				dispatch({ type: "SET_STAFF_RECRUITMENT", payload: data });
			} catch (error) {
				throw error;
			}
		}

		async function loadSlots() {
			try {
				const { data } = await axios.get(`${api}/slot`);
				const slots = data.map(slot => new Slot(slot.destination, slot.price, { id: slot.id }));
				dispatch({ type: "SET_SLOTS", slots });
			} catch (error) {
				throw error;
			}
		}

		async function loadFlightPlans() {
			try {
				const { data: plans } = await axios.get(`${api}/flightPlan`);
				const { data: flights } = await axios.get(`${api}/flight`);
				return [plans, flights];
			} catch (error) {
				throw error;
			}
		}

		async function loadStaff() {
			try {
				const { data } = await axios.get(`${api}/staff`);
				dispatch({ type: "SET_STAFF", payload: data.map(w => Staff.createWorker(w)).filter(w => w) });
				setTimeout(Staff.init, 0);
			} catch (error) {
				if (error.response?.status === 404) {
					dispatch({ type: "SET_STAFF", payload: [] });
					return;
				}

				throw error;
			}
		}

		async function loadConfigurations() {
			try {
				const { data } = await axios.get(`${api}/configuration`);
				dispatch({ type: "SET_CONFIGURATIONS", payload: data });
			} catch (error) {
				throw error;
			}
		}

		function handleFlightPlans(plans, flights) {
			const flightPlans = plans.map(p => FlightPlan.fromObject({ ...p, savedInRedux: true }));
			dispatch({ type: "SET_FLIGHT_PLANS", flightPlans });

			flights.forEach(f => {
				const i = plans.findIndex(p => p.id === f.scheduleId);
				plans[i].flights && !plans[i].flights.includes(f.id) && plans[i].flights.push(f.id);
				plans[i].flights ||= [f.id];
			});

			const flightsObj = flights.map(p => Flight.fromObject(p));
			dispatch({ type: "SET_FLIGHTS", flights: flightsObj });
		}

		async function fetchAndLoadCateringData() {
			try {
				const { data: cateringInfo } = await axios.get(`${api}/catering`);
				dispatch({ type: "SET_CATERING", state: cateringInfo });
			} catch (error) {
				if (error.response.status === 404) {
					dispatch({ type: "SET_CATERING", state: { supplierId: null, settings: {} } });
				} else {
					throw error;
				}
			}
		}
	}, [dispatch, mapInfo.type, i, dataLoaded]);

	return { isLoading, dataLoaded, error, retry };
};

export default useLoadGameData;
